import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Select,
  Button,
  Alert,
  Badge,
  Paper,
  Loader,
  Title
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';
import { api } from '../../services/api';
import { notifications } from '@mantine/notifications';

const SubscriptionManager = ({ opened, onClose, userId, username }) => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('');

  const plans = [
    { value: 'paypiggie', label: 'PayPiggie ($15/month)' },
    { value: 'paypiggiepro', label: 'PayPiggiePro ($25/month)' }
  ];

  useEffect(() => {
    if (opened && userId) {
      fetchSubscription();
    }
  }, [opened, userId]);

  const fetchSubscription = async () => {
    try {
      setFetching(true);
      setError(null);
      const response = await api.get(`/admin/users/${userId}/subscription/`);
      setSubscription(response.subscription);
      if (response.subscription) {
        setSelectedPlan(response.subscription.plan);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError(err.message || 'Failed to fetch subscription');
    } finally {
      setFetching(false);
    }
  };

  const handleActivateSubscription = async () => {
    if (!selectedPlan) {
      setError('Please select a subscription plan');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post(`/admin/users/${userId}/subscription/`, {
        plan: selectedPlan,
        action: 'activate'
      });

      notifications.show({
        title: 'Success',
        message: response.message || 'Subscription activated successfully',
        color: 'green',
        icon: <IconCheck size="1rem" />
      });

      fetchSubscription();
    } catch (err) {
      console.error('Error activating subscription:', err);
      setError(err.message || 'Failed to activate subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post(`/admin/users/${userId}/subscription/`, {
        action: 'cancel'
      });

      notifications.show({
        title: 'Success',
        message: response.message || 'Subscription canceled successfully',
        color: 'yellow',
        icon: <IconX size="1rem" />
      });

      fetchSubscription();
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleExpireSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post(`/admin/users/${userId}/subscription/`, {
        action: 'expire'
      });

      notifications.show({
        title: 'Success',
        message: response.message || 'Subscription expired successfully',
        color: 'red',
        icon: <IconX size="1rem" />
      });

      fetchSubscription();
    } catch (err) {
      console.error('Error expiring subscription:', err);
      setError(err.message || 'Failed to expire subscription');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'green',
      canceled: 'yellow',
      expired: 'red',
      pending: 'gray'
    };
    return colors[status] || 'gray';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Manage Subscription - ${username}`}
      size="lg"
    >
      <Stack>
        {error && (
          <Alert icon={<IconAlertCircle size="1rem" />} color="red">
            {error}
          </Alert>
        )}

        {fetching ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <Loader />
          </div>
        ) : (
          <>
            {/* Current Subscription Status */}
            <Paper p="md" withBorder>
              <Title order={5} mb="sm">Current Subscription</Title>
              {subscription ? (
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text>Plan:</Text>
                    <Badge size="lg" variant="filled">
                      {subscription.plan === 'paypiggie' ? 'PayPiggie' : 'PayPiggiePro'}
                    </Badge>
                  </Group>
                  <Group justify="space-between">
                    <Text>Status:</Text>
                    <Badge color={getStatusColor(subscription.status)} variant="light">
                      {subscription.status}
                    </Badge>
                  </Group>
                  <Group justify="space-between">
                    <Text>Price:</Text>
                    <Text fw={600}>${subscription.price}/month</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text>Period Start:</Text>
                    <Text>{formatDate(subscription.current_period_start)}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text>Period End:</Text>
                    <Text>{formatDate(subscription.current_period_end)}</Text>
                  </Group>
                  {subscription.canceled_at && (
                    <Group justify="space-between">
                      <Text>Canceled At:</Text>
                      <Text>{formatDate(subscription.canceled_at)}</Text>
                    </Group>
                  )}
                </Stack>
              ) : (
                <Text c="dimmed" ta="center" py="md">
                  No active subscription
                </Text>
              )}
            </Paper>

            {/* Subscription Management */}
            <Paper p="md" withBorder>
              <Title order={5} mb="sm">Manage Subscription</Title>
              
              <Stack>
                <Select
                  label="Select Plan"
                  placeholder="Choose a subscription plan"
                  data={plans}
                  value={selectedPlan}
                  onChange={setSelectedPlan}
                  disabled={loading}
                />

                <Group grow>
                  <Button
                    onClick={handleActivateSubscription}
                    loading={loading}
                    disabled={!selectedPlan}
                    color="green"
                  >
                    {subscription ? 'Update' : 'Activate'} Subscription
                  </Button>
                </Group>

                {subscription && subscription.status === 'active' && (
                  <Group grow>
                    <Button
                      onClick={handleCancelSubscription}
                      loading={loading}
                      color="yellow"
                      variant="light"
                    >
                      Cancel Subscription
                    </Button>
                    <Button
                      onClick={handleExpireSubscription}
                      loading={loading}
                      color="red"
                      variant="light"
                    >
                      Expire Now
                    </Button>
                  </Group>
                )}
              </Stack>
            </Paper>

            <Text size="xs" c="dimmed" ta="center">
              Note: Admin-managed subscriptions bypass payment processing
            </Text>
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default SubscriptionManager;